import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useMemo } from "react";

import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@krea/shared/design-system/Select";
import { toast } from "@krea/shared/design-system/utils/toast";

import { LenderUserRoleEnum } from "@/features/common/enums";
import { queryKeyMap } from "@/features/query-client";
import { useGetLenderCompanyUsers } from "@/features/users/hooks/use-get-lender-company-users";
import { useUpdateUserCompanySettingsMutation } from "@/features/users/hooks/use-update-user-company-settings.mutation";
import type { ILenderUser } from "@/features/users/types";

import { useGetCurrentLenderCompanyQuery } from "../../hooks/use-get-lender-company.query";

interface ITeamMemberRoleCellProps {
  user: ILenderUser;
}

export const TeamMemberRoleCell = ({ user }: ITeamMemberRoleCellProps) => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { mutate: updateUserCompanySettings } =
    useUpdateUserCompanySettingsMutation();
  const queryClient = useQueryClient();

  const { data: usersData } = useGetLenderCompanyUsers(
    Number(lenderCompany?.id),
  );

  const totalAdmins = useMemo(
    () =>
      usersData?.filter(
        (user) => user.settings?.role === LenderUserRoleEnum.ADMIN,
      ).length ?? 0,
    [usersData],
  );

  const handleValueChange = (value: LenderUserRoleEnum) => {
    if (!lenderCompany?.id) return;

    updateUserCompanySettings(
      {
        lenderCompanyId: lenderCompany.id,
        userId: user.id,
        data: { role: value },
      },
      {
        onSuccess: () => {
          toast.success(
            `User ${user.firstName} ${user.lastName} role updated to ${value}`,
          );
          queryClient.invalidateQueries({
            queryKey: queryKeyMap.getLenderCompanyUsers(lenderCompany.id),
          });
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={user.settings?.role}
        onValueChange={(value) => {
          handleValueChange(value as LenderUserRoleEnum);
        }}
        disabled={
          totalAdmins === 1 && user.settings?.role === LenderUserRoleEnum.ADMIN
        }
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.values(LenderUserRoleEnum).map((role) => (
            <SelectItem key={role} value={role}>
              {role}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {totalAdmins === 1 &&
        user.settings?.role === LenderUserRoleEnum.ADMIN && (
          <ResponsiveTooltip
            trigger={<AlertTriangle className="size-4 text-orange" />}
          >
            You cannot remove the last admin from the team. Make sure to have at
            least one admin in the team.
          </ResponsiveTooltip>
        )}
    </div>
  );
};
